// src/services/customerService.ts

import axiosInstance from "./axios";
import { Claim, Policy, Order, Analytics } from "@/types";

function normalizeClaimMode(value: unknown): "resume" | "submitted" | undefined {
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return normalizeClaimMode(record.mode ?? record.value ?? record.type);
  }
  const mode = String(value ?? "").trim().toLowerCase();
  if (mode === "resume" || mode === "submitted") return mode;
  return undefined;
}

function readClaimId(item: any): string | undefined {
  const raw = item?.claim_id ?? item?.claimId ?? item?.active_claim_id;
  if (raw == null) return undefined;
  const id = String(raw).trim();
  if (!id || id === "null" || id === "undefined") return undefined;
  return id;
}

function readMode(item: any): "resume" | "submitted" | undefined {
  if (!item || typeof item !== "object") return undefined;
  return normalizeClaimMode(item.mode);
}

export async function getCustomerPolicies(): Promise<Policy[]> {
  try {
    const response = await axiosInstance.get<{
      success: boolean;
      message: string;
      data: any[];
    }>("/api/customer/getPolicies");

    const transformedPolicies: Policy[] = response.data.data.map((item) => {
      const {
        id,
        policy_number,
        premium_amount,
        status,
        certificate_url,
        product_image,
        product_title,
        product_price,
        policy_type,
        end_date,
        timeline: tl,
      } = item;

      // build a timeline just like you did for claims
      const formattedTimeline: { date: string; event: string; details: string }[] = [];

      if (tl?.created_at) {
        formattedTimeline.push({
          date: tl.created_at,
          event: "Policy Created",
          details: "Your policy was created.",
        });
      }

      if (tl?.start_date) {
        formattedTimeline.push({
          date: tl.start_date,
          event: "Coverage Started",
          details: "Your coverage period began.",
        });
      }

      if (tl?.end_date) {
        formattedTimeline.push({
          date: tl.end_date,
          event: "Coverage Ends",
          details: "Your coverage period will end.",
        });
      }

      const claimId = readClaimId(item);

      return {
        id,
        name: policy_number,
        type: policy_type,
        premium: premium_amount,
        status: status as Policy["status"],
        expiryDate: end_date,
        certificateUrl: certificate_url ?? undefined,
        productName: product_title,
        productPrice: product_price,
        imageUrl: product_image ?? undefined,
        timeline: formattedTimeline,
        merchantTerms: item.merchant_terms,
        merchantName: item.merchant_name,
        merchantUrl: item.merchant_url,
        merchantTncUrl: item.merchant_tnc_url ?? item.tnc_url ?? undefined,
        mode: claimId
          ? readMode(item) || (item.submitted === true ? "submitted" : "resume")
          : undefined,
        claimId,
      };
    });

    return transformedPolicies;
  } catch (err: any) {
    console.error("Error fetching customer policies:", err);
    throw err;
  }
}

export async function getCustomerClaims(): Promise<Claim[]> {
  try {
    const response = await axiosInstance.get<{
      success: boolean;
      message: string;
      data: any[];
    }>("/api/customer/getClaims");

    const transformedClaims: Claim[] = response.data.data.map((item) => {
      const {
        id,
        policyId,
        type,
        description,
        status,
        created_at,
        CoverageAmount,
        product_title,
        evidence,
        productOriginalImage,
        timeline,
      } = item;

      const formattedTimeline: Claim["timeline"] = [];
      const statusLower = String(status || "").toLowerCase();
      const createdAt = created_at || timeline?.created_at;
      const updatedAt = timeline?.updated_at;
      const reviewedAt = timeline?.reviewed_at;
      const processedAt = timeline?.processed_at;

      const sameInstant = (a?: string, b?: string) => {
        if (!a || !b) return false;
        const left = new Date(a).getTime();
        const right = new Date(b).getTime();
        if (Number.isNaN(left) || Number.isNaN(right)) return false;
        return Math.abs(left - right) < 2000;
      };

      if (createdAt) {
        formattedTimeline.push({
          date: createdAt,
          event: "Claim Submitted",
          details: "Claim was filed by the user.",
        });
      }

      if (updatedAt && !sameInstant(updatedAt, createdAt)) {
        formattedTimeline.push({
          date: updatedAt,
          event: "Updated",
          details: "Claim information was updated.",
        });
      }

      const reachedReview = ["under_review", "approved", "rejected"].includes(
        statusLower,
      );
      if (reachedReview && reviewedAt && !sameInstant(reviewedAt, createdAt)) {
        formattedTimeline.push({
          date: reviewedAt,
          event: "Reviewed",
          details: "Claim was reviewed by our team.",
        });
      }

      if (statusLower === "approved" && processedAt) {
        formattedTimeline.push({
          date: processedAt,
          event: "Claim Approved",
          details: "Congratulations! Your claim was approved.",
        });
      } else if (statusLower === "rejected" && processedAt) {
        formattedTimeline.push({
          date: processedAt,
          event: "Claim Rejected",
          details: "Unfortunately, your claim was rejected.",
        });
      }

      const submitted =
        typeof item.submitted === "boolean"
          ? item.submitted
          : typeof item.is_submitted === "boolean"
            ? item.is_submitted
            : undefined;

      const mode =
        readMode(item) ||
        (submitted === true
          ? "submitted"
          : String(status || "").toLowerCase() === "cancelled"
            ? undefined
            : String(status || "").toLowerCase() === "pending" || submitted === false
              ? "resume"
              : status
                ? "submitted"
                : undefined);
      const linkedPolicyId =
        policyId ??
        item.policy_id ??
        item.policyID ??
        item.policy?.id ??
        item.policy?.policy_id;

      return {
        id,
        policyId: linkedPolicyId != null ? String(linkedPolicyId) : linkedPolicyId,
        policyNumber: item.policy_number ?? item.policyNumber,
        mode,
        productName: product_title || "Unnamed Product",
        filedAt: created_at,
        claimType: type,
        description: description ?? "No description provided",
        amount: CoverageAmount ?? 0,
        status: status as Claim["status"],
        submitted,
        evidence: evidence ?? [],
        protection:
          type === "shipping" ? "Shipping Protection" : "Product Protection",
        imageUrl: productOriginalImage ?? undefined,
        timeline: formattedTimeline,
      };
    });

    return transformedClaims;
  } catch (err: any) {
    console.error("Error fetching customer claims:", err);
    throw err;
  }
}

export async function getCustomerOrders(): Promise<Order[]> {
  try {
    const response = await axiosInstance.post<{
      success: boolean;
      orders: any[];
    }>("/api/customer/fetchCustomerOrderHistory");

    const { orders } = response.data;

    if (!orders) return [];

    return orders.map((o: any) => ({
      order_id: o.order_id,
      product_id: o.product_id,
      created_at: o.created_at,
      product_title: o.product_title || "Unknown Product",
      product_image: o.product_image || "",
      product_price: o.product_price || 0,
      return_status: o.return_status,
      returns_active: o.returns_active,
      policy: o.policy,
      // Passed through for the My Plans product cards (merchant, delivery and tracking).
      human_readable_order_id: o.human_readable_order_id,
      merchant_name: o.merchant_name ?? o.shop_name ?? o.merchant?.name,
      fulfillment_status: o.fulfillment_status,
      delivered_at: o.delivered_at,
      courier_metadata: o.courier_metadata,
      shipping_address: o.shipping_address,
    }));
  } catch (err: any) {
    console.error("Error fetching customer orders:", err);
    throw err;
  }
}


export async function getCustomerAnalytics(): Promise<Analytics> {
  try {
    const response = await axiosInstance.get<Analytics>("/api/customer/getMetrics");
    return response.data;
  } catch (err: any) {
    console.error("Error fetching customer analytics:", err);
    throw err;
  }
}

export async function getReplacementSuggestions(merchantId: string, targetPrice: number, targetProductId?: string): Promise<any[]> {
  try {
    const response = await axiosInstance.post<{
      success: boolean;
      data: any[];
    }>("/api/customer/getReplacementSuggestions", {
      merchant_id: merchantId,
      target_price: targetPrice,
      target_product_id: targetProductId,
    });
    return response.data.data;
  } catch (err: any) {
    console.error("Error fetching replacement suggestions:", err);
    throw err;
  }
}
export async function getOrdersByIdentifier(identifier: string): Promise<Order[]> {
  try {
    const payload: any = {};
    if (identifier.includes("@")) {
      payload.email = identifier;
    } else {
      payload.mobile_number = identifier;
    }

    const response = await axiosInstance.post<{
      success: boolean;
      orders: any[];
    }>("/api/customer/fetch-orders-by-customer", payload);

    const { orders } = response.data;
    if (!orders) return [];

    return orders.map((o: any) => ({
      order_id: o.order_id,
      product_id: o.product_id,
      created_at: o.created_at,
      product_title: o.product_title || "Unknown Product",
      product_image: o.product_image || "",
      product_price: o.product_price || 0,
      return_status: o.return_status,
      fulfillment_status: o.fulfillment_status,
      delivered_at: o.delivered_at,
      returns_active: o.returns_active,
      policy: o.policy,
    }));
  } catch (err: any) {
    console.error("Error fetching orders by identifier:", err);
    throw err;
  }
}
